import { AnimatedDialog, AnimatedDialogContent, AnimatedDialogDescription, AnimatedDialogFooter, AnimatedDialogHeader, AnimatedDialogTitle } from "components/ui/animated-dialog";
import { Button } from "components/ui/button";
import { Input } from "components/ui/input";
import { Label } from "components/ui/label";
import { Textarea } from "components/ui/textarea";
import { Tooltip } from "components/ui/tooltip";
import { Info } from "lucide-react";
import React, { useState } from "react";
import { graphql, useMutation } from "react-relay";
import { useToast } from "src/hooks/use-toast";
import { useProductBranding } from "src/hooks/useProductBranding";

import type { EditMemoryModalMutation } from "./__generated__/EditMemoryModalMutation.graphql";

const editMemoryMutation = graphql`
  mutation EditMemoryModalMutation($input: CorrectOrganizationalMemoryInput!) {
    correctOrganizationalMemory(input: $input) {
      __typename
      ... on CorrectOrganizationalMemorySuccess {
        memory {
          id
          memoryType
          source
          score
          body {
            __typename
            ... on GraphQLGenericLearningBody {
              type
              scope
              knowledge
              reasoning
            }
            ... on GraphQLQueryBody {
              queryType
              question
              queryDescription
              query
            }
            ... on GraphQLResourceBody {
              resourceType
              name
              content
              systemDescription
            }
          }
        }
      }
      ... on CorrectOrganizationalMemoryError {
        message
      }
    }
  }
`;

type GenericLearningBodyType = {
  __typename: "GraphQLGenericLearningBody";
  type?: string | null;
  scope?: string | null;
  knowledge?: string | null;
  reasoning?: string | null;
};

type QueryBodyType = {
  __typename: "GraphQLQueryBody";
  queryType?: string | null;
  question?: string | null;
  queryDescription?: string | null;
  query?: any;
};

type ResourceBodyType = {
  __typename: "GraphQLResourceBody";
  resourceType?: string | null;
  name?: string | null;
  content?: any;
  systemDescription?: string | null;
};

type MemoryBodyType = GenericLearningBodyType | QueryBodyType | ResourceBodyType | { __typename: "%other" };

type EditMemoryModalPropsType = {
  isOpen: boolean;
  onClose: () => void;
  memory: {
    id: string;
    body: MemoryBodyType;
  };
  onSuccess?: () => void;
  title?: string;
  description?: string;
};

export const EditMemoryModal = React.memo<EditMemoryModalPropsType>(function EditMemoryModal({
  isOpen,
  onClose,
  memory,
  onSuccess,
  title = "Edit Context Snippet",
  description = "Update the content of this context snippet. Changes will be reflected in future searches.",
}) {
  const { toast } = useToast();
  const { productName } = useProductBranding();
  const [commitEdit, isEditing] = useMutation<EditMemoryModalMutation>(editMemoryMutation);

  // Initialize form state based on body type
  const isGenericLearning = memory.body.__typename === "GraphQLGenericLearningBody";
  const isQuery = memory.body.__typename === "GraphQLQueryBody";
  const isResource = memory.body.__typename === "GraphQLResourceBody";

  const [formData, setFormData] = useState(() => {
    if (isGenericLearning && memory.body.__typename === "GraphQLGenericLearningBody") {
      return {
        scope: memory.body.scope || "",
        knowledge: memory.body.knowledge || "",
        queryType: "",
        queryDescription: "",
        query: "",
        resourceDescription: "",
        resourceTags: [] as string[],
        systemDescription: "",
      };
    }
    if (isQuery && memory.body.__typename === "GraphQLQueryBody") {
      // Format query for display - if it's an object, stringify it nicely
      let queryDisplay: string;
      if (typeof memory.body.query === "string") {
        queryDisplay = memory.body.query;
      } else if (memory.body.query && typeof memory.body.query === "object") {
        queryDisplay = JSON.stringify(memory.body.query, null, 2);
      } else {
        queryDisplay = "";
      }

      return {
        scope: "",
        knowledge: "",
        queryType: memory.body.queryType || "",
        queryDescription: memory.body.queryDescription || memory.body.question || "",
        query: queryDisplay,
        resourceDescription: "",
        resourceTags: [] as string[],
        systemDescription: "",
      };
    }
    if (isResource && memory.body.__typename === "GraphQLResourceBody") {
      if (memory.body.resourceType === "metric") {
        return {
          scope: "",
          knowledge: "",
          queryType: "",
          queryDescription: "",
          query: "",
          resourceDescription: memory.body.content?.description || "",
          resourceTags: memory.body.content?.tags || [],
          systemDescription: "",
        };
      }
      if (memory.body.resourceType === "nr_application") {
        return {
          scope: "",
          knowledge: "",
          queryType: "",
          queryDescription: "",
          query: "",
          resourceDescription: "",
          resourceTags: [] as string[],
          systemDescription: memory.body.systemDescription || "",
        };
      }
      if (memory.body.resourceType === "github_repository") {
        return {
          scope: "",
          knowledge: "",
          queryType: "",
          queryDescription: "",
          query: "",
          resourceDescription: "",
          resourceTags: [] as string[],
          systemDescription: memory.body.systemDescription || "",
        };
      }
      if (memory.body.resourceType === "buildkite_pipeline" || memory.body.resourceType === "buildkite_test_suite") {
        return {
          scope: "",
          knowledge: "",
          queryType: "",
          queryDescription: "",
          query: "",
          resourceDescription: "",
          resourceTags: [] as string[],
          systemDescription: memory.body.systemDescription || "",
        };
      }
    }
    return { scope: "", knowledge: "", queryType: "", queryDescription: "", query: "", resourceDescription: "", resourceTags: [] as string[], systemDescription: "" };
  });

  const [correctionReason, setCorrectionReason] = useState("");
  const [tagInput, setTagInput] = useState("");

  // Reset form state when modal opens or memory changes
  React.useEffect(() => {
    if (isOpen) {
      if (isGenericLearning && memory.body.__typename === "GraphQLGenericLearningBody") {
        setFormData({
          scope: memory.body.scope || "",
          knowledge: memory.body.knowledge || "",
          queryType: "",
          queryDescription: "",
          query: "",
          resourceDescription: "",
          resourceTags: [] as string[],
          systemDescription: "",
        });
      } else if (isQuery && memory.body.__typename === "GraphQLQueryBody") {
        let queryDisplay: string;
        if (typeof memory.body.query === "string") {
          queryDisplay = memory.body.query;
        } else if (memory.body.query && typeof memory.body.query === "object") {
          queryDisplay = JSON.stringify(memory.body.query, null, 2);
        } else {
          queryDisplay = "";
        }

        setFormData({
          scope: "",
          knowledge: "",
          queryType: memory.body.queryType || "",
          queryDescription: memory.body.queryDescription || memory.body.question || "",
          query: queryDisplay,
          resourceDescription: "",
          resourceTags: [] as string[],
          systemDescription: "",
        });
      } else if (isResource && memory.body.__typename === "GraphQLResourceBody") {
        if (memory.body.resourceType === "metric") {
          setFormData({
            scope: "",
            knowledge: "",
            queryType: "",
            queryDescription: "",
            query: "",
            resourceDescription: memory.body.content?.description || "",
            resourceTags: memory.body.content?.tags || [],
            systemDescription: "",
          });
        } else if (memory.body.resourceType === "nr_application") {
          setFormData({
            scope: "",
            knowledge: "",
            queryType: "",
            queryDescription: "",
            query: "",
            resourceDescription: "",
            resourceTags: [] as string[],
            systemDescription: memory.body.systemDescription || "",
          });
        } else if (memory.body.resourceType === "github_repository") {
          setFormData({
            scope: "",
            knowledge: "",
            queryType: "",
            queryDescription: "",
            query: "",
            resourceDescription: "",
            resourceTags: [] as string[],
            systemDescription: memory.body.systemDescription || "",
          });
        } else if (memory.body.resourceType === "buildkite_pipeline" || memory.body.resourceType === "buildkite_test_suite") {
          setFormData({
            scope: "",
            knowledge: "",
            queryType: "",
            queryDescription: "",
            query: "",
            resourceDescription: "",
            resourceTags: [] as string[],
            systemDescription: memory.body.systemDescription || "",
          });
        }
      }
      setCorrectionReason("");
      setTagInput("");
    }
  }, [isOpen, memory, isGenericLearning, isQuery, isResource]);

  const handleAddTag = () => {
    const trimmedTag = tagInput.trim();
    if (trimmedTag && !formData.resourceTags.includes(trimmedTag)) {
      setFormData({ ...formData, resourceTags: [...formData.resourceTags, trimmedTag] });
      setTagInput("");
    }
  };

  const handleRemoveTag = (tagToRemove: string) => {
    setFormData({
      ...formData,
      resourceTags: formData.resourceTags.filter((tag: string) => tag !== tagToRemove),
    });
  };

  const handleSubmit = () => {
    if (isGenericLearning) {
      if (!formData.scope.trim() || !formData.knowledge.trim()) {
        toast({
          title: "Error",
          description: "Scope and knowledge are required",
          variant: "error",
        });
        return;
      }
    }

    if (isQuery) {
      if (!formData.queryDescription.trim()) {
        toast({
          title: "Error",
          description: "Query description is required",
          variant: "error",
        });
        return;
      }
    }

    // Prepare body JSON based on type
    let bodyJson;
    if (isGenericLearning && memory.body.__typename === "GraphQLGenericLearningBody") {
      bodyJson = {
        type: memory.body.type,
        scope: formData.scope,
        knowledge: formData.knowledge,
      };
    } else if (isQuery && memory.body.__typename === "GraphQLQueryBody") {
      // Query type and query content are read-only, only description is editable
      bodyJson = {
        query_type: memory.body.queryType,  // Use original (read-only)
        question: formData.queryDescription,
        query_description: formData.queryDescription,
        query: memory.body.query,  // Use original (read-only)
      };
    } else if (isResource && memory.body.__typename === "GraphQLResourceBody") {
      if (memory.body.resourceType === "metric") {
        // For metric resources, only description and tags are editable
        bodyJson = {
          resource_type: memory.body.resourceType,  // Use original (read-only)
          name: memory.body.name,  // Use original (read-only)
          content: {
            ...(memory.body.content || {}),  // Preserve all existing content fields
            description: formData.resourceDescription,
            tags: formData.resourceTags,
          },
        };
      } else if (memory.body.resourceType === "nr_application") {
        // For NR application resources, only system_description is editable
        bodyJson = {
          resource_type: memory.body.resourceType,  // Use original (read-only)
          name: memory.body.name,  // Use original (read-only)
          content: memory.body.content,  // Use original (read-only)
          system_description: formData.systemDescription,
        };
      } else if (memory.body.resourceType === "github_repository") {
        // For GitHub repository resources, only system_description is editable
        bodyJson = {
          resource_type: memory.body.resourceType,  // Use original (read-only)
          name: memory.body.name,  // Use original (read-only)
          content: memory.body.content,  // Use original (read-only)
          system_description: formData.systemDescription,
        };
      } else if (memory.body.resourceType === "buildkite_pipeline" ||
                 memory.body.resourceType === "buildkite_test_suite") {
        // For Buildkite resources, only system_description is editable
        bodyJson = {
          resource_type: memory.body.resourceType,  // Use original (read-only)
          name: memory.body.name,  // Use original (read-only)
          content: memory.body.content,  // Use original (read-only)
          system_description: formData.systemDescription,
        };
      } else {
        bodyJson = memory.body;
      }
    } else {
      bodyJson = memory.body;
    }

    commitEdit({
      variables: {
        input: {
          memoryId: memory.id,
          body: bodyJson,
          correctionReason: correctionReason.trim() || null,
        },
      },
      onCompleted: (response) => {
        if (response.correctOrganizationalMemory.__typename === "CorrectOrganizationalMemorySuccess") {
          toast({
            title: "Success",
            description: `Context snippet updated successfully. The corrected snippet will be searchable after indexing, so it may take a couple minutes to appear in search results or be accessible by ${productName}.`,
            variant: "default",
          });
          onSuccess?.();
          onClose();
        } else if (response.correctOrganizationalMemory.__typename === "CorrectOrganizationalMemoryError") {
          toast({
            title: "Error",
            description: response.correctOrganizationalMemory.message,
            variant: "error",
          });
        }
      },
      onError: (error) => {
        toast({
          title: "Error",
          description: error.message,
          variant: "error",
        });
      },
    });
  };

  return (
    <AnimatedDialog open={isOpen} onOpenChange={onClose}>
      <AnimatedDialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <AnimatedDialogHeader>
          <AnimatedDialogTitle>{title}</AnimatedDialogTitle>
          <AnimatedDialogDescription>
            {description}
          </AnimatedDialogDescription>
        </AnimatedDialogHeader>

        <div className="space-y-4 py-4">
          {isQuery && (
            <>
              <div className="space-y-2">
                <Label htmlFor="queryType">Query Type</Label>
                <Input
                  id="queryType"
                  value={formData.queryType}
                  disabled
                  readOnly
                  className="bg-card cursor-not-allowed"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="queryDescription">
                  Query Description <span className="text-red-400">*</span>
                </Label>
                <Textarea
                  id="queryDescription"
                  value={formData.queryDescription}
                  onChange={(e) => setFormData({ ...formData, queryDescription: e.target.value })}
                  placeholder="Describe what this query does and when to use it"
                  disabled={isEditing}
                  required
                  rows={3}
                  className="resize-y"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="query">Query</Label>
                <Textarea
                  id="query"
                  value={formData.query}
                  disabled
                  readOnly
                  rows={8}
                  className="resize-y font-mono text-sm bg-card cursor-not-allowed"
                />
              </div>
            </>
          )}

          {isGenericLearning && (
            <>
              <div className="space-y-2">
                <Label htmlFor="scope">
                  Scope <span className="text-red-400">*</span>
                </Label>
                <Input
                  id="scope"
                  value={formData.scope}
                  onChange={(e) => setFormData({ ...formData, scope: e.target.value })}
                  placeholder="e.g., team name, project name, or 'organization-wide'"
                  disabled={isEditing}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="knowledge">
                  Knowledge <span className="text-red-400">*</span>
                </Label>
                <Textarea
                  id="knowledge"
                  value={formData.knowledge}
                  onChange={(e) => setFormData({ ...formData, knowledge: e.target.value })}
                  placeholder="Describe the knowledge or best practice"
                  disabled={isEditing}
                  required
                  rows={10}
                  className="resize-y"
                />
              </div>
            </>
          )}

          {isResource && memory.body.__typename === "GraphQLResourceBody" && memory.body.resourceType === "metric" && (
            <>
              <div className="space-y-2">
                <Label htmlFor="resourceType">Resource Type</Label>
                <Input
                  id="resourceType"
                  value={memory.body.resourceType || ""}
                  disabled
                  readOnly
                  className="bg-card cursor-not-allowed"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="resourceName">Name</Label>
                <Input
                  id="resourceName"
                  value={memory.body.name || ""}
                  disabled
                  readOnly
                  className="bg-card cursor-not-allowed"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="resourceDescription">Description</Label>
                <Textarea
                  id="resourceDescription"
                  value={formData.resourceDescription}
                  onChange={(e) => setFormData({ ...formData, resourceDescription: e.target.value })}
                  placeholder="Describe this metric"
                  disabled={isEditing}
                  rows={4}
                  className="resize-y"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="resourceTags">Tags</Label>
                <div className="space-y-2">
                  {formData.resourceTags.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {formData.resourceTags.map((tag: string, idx: number) => (
                        <div key={idx} className="flex items-center gap-1 px-2 py-1 bg-accent rounded text-sm">
                          <span>{tag}</span>
                          <button
                            type="button"
                            onClick={() => handleRemoveTag(tag)}
                            className="text-muted-foreground hover:text-red-400 ml-1"
                            disabled={isEditing}
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="flex gap-2">
                    <Input
                      id="resourceTags"
                      value={tagInput}
                      onChange={(e) => setTagInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          handleAddTag();
                        }
                      }}
                      placeholder="Add a tag and press Enter"
                      disabled={isEditing}
                    />
                    <Button
                      type="button"
                      onClick={handleAddTag}
                      disabled={isEditing || !tagInput.trim()}
                      variant="outline"
                    >
                      Add
                    </Button>
                  </div>
                </div>
              </div>
            </>
          )}

          {isResource && memory.body.__typename === "GraphQLResourceBody" && memory.body.resourceType === "nr_application" && (
            <>
              <div className="space-y-2">
                <Label htmlFor="resourceType">Resource Type</Label>
                <Input
                  id="resourceType"
                  value={memory.body.resourceType || ""}
                  disabled
                  readOnly
                  className="bg-card cursor-not-allowed"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="resourceName">Name</Label>
                <Input
                  id="resourceName"
                  value={memory.body.name || ""}
                  disabled
                  readOnly
                  className="bg-card cursor-not-allowed"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="systemDescription">Additional Context</Label>
                <Textarea
                  id="systemDescription"
                  value={formData.systemDescription}
                  onChange={(e) => setFormData({ ...formData, systemDescription: e.target.value })}
                  placeholder="Add additional context for this New Relic application"
                  disabled={isEditing}
                  rows={4}
                  className="resize-y"
                />
              </div>
            </>
          )}

          {isResource && memory.body.__typename === "GraphQLResourceBody" && memory.body.resourceType === "github_repository" && (
            <>
              <div className="space-y-2">
                <Label htmlFor="resourceType">Resource Type</Label>
                <Input
                  id="resourceType"
                  value={memory.body.resourceType || ""}
                  disabled
                  readOnly
                  className="bg-card cursor-not-allowed"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="resourceName">Repository Name</Label>
                <Input
                  id="resourceName"
                  value={memory.body.name || ""}
                  disabled
                  readOnly
                  className="bg-card cursor-not-allowed"
                />
              </div>

              {memory.body.content?.url && (
                <div className="space-y-2">
                  <Label htmlFor="repoUrl">URL</Label>
                  <Input
                    id="repoUrl"
                    value={memory.body.content.url || ""}
                    disabled
                    readOnly
                    className="bg-card cursor-not-allowed"
                  />
                </div>
              )}

              {memory.body.content?.description && (
                <div className="space-y-2">
                  <Label htmlFor="repoDescription">GitHub Description</Label>
                  <Textarea
                    id="repoDescription"
                    value={memory.body.content.description || ""}
                    disabled
                    readOnly
                    className="bg-card cursor-not-allowed"
                    rows={2}
                  />
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="systemDescription">Additional Context</Label>
                <Textarea
                  id="systemDescription"
                  value={formData.systemDescription}
                  onChange={(e) => setFormData({ ...formData, systemDescription: e.target.value })}
                  placeholder={`Add additional context for this GitHub repository to provide additional context for ${productName}`}
                  disabled={isEditing}
                  rows={4}
                  className="resize-y"
                />
              </div>
            </>
          )}

          {isResource && memory.body.__typename === "GraphQLResourceBody" &&
           (memory.body.resourceType === "buildkite_pipeline" || memory.body.resourceType === "buildkite_test_suite") && (
            <>
              <div className="space-y-2">
                <Label htmlFor="resourceType">Resource Type</Label>
                <Input
                  id="resourceType"
                  value={memory.body.resourceType || ""}
                  disabled
                  readOnly
                  className="bg-card cursor-not-allowed"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="resourceName">Name</Label>
                <Input
                  id="resourceName"
                  value={memory.body.name || ""}
                  disabled
                  readOnly
                  className="bg-card cursor-not-allowed"
                />
              </div>

              {memory.body.content?.url && (
                <div className="space-y-2">
                  <Label htmlFor="buildkiteUrl">URL</Label>
                  <Input
                    id="buildkiteUrl"
                    value={memory.body.content.url || ""}
                    disabled
                    readOnly
                    className="bg-card cursor-not-allowed"
                  />
                </div>
              )}

              {memory.body.content?.description && (
                <div className="space-y-2">
                  <Label htmlFor="buildkiteDescription">Description</Label>
                  <Textarea
                    id="buildkiteDescription"
                    value={memory.body.content.description || ""}
                    disabled
                    readOnly
                    className="bg-card cursor-not-allowed"
                    rows={2}
                  />
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="systemDescription">Additional Context</Label>
                <Textarea
                  id="systemDescription"
                  value={formData.systemDescription}
                  onChange={(e) => setFormData({ ...formData, systemDescription: e.target.value })}
                  placeholder={`Add additional context for this Buildkite resource to provide additional context for ${productName}`}
                  disabled={isEditing}
                  rows={4}
                  className="resize-y"
                />
              </div>
            </>
          )}

          <div className="space-y-2">
            <div className="flex items-center gap-1">
              <Label htmlFor="correction-reason">Correction Reason</Label>
              <Tooltip content="Let us know why you are correcting this context snippet to help improve your organization's context generation in the future" asChild>
                <Info className="w-3 h-3 text-muted-foreground" />
              </Tooltip>
            </div>
            <Textarea
              id="correction-reason"
              value={correctionReason}
              onChange={(e) => setCorrectionReason(e.target.value)}
              placeholder="Explain why this context snippet is being corrected"
              disabled={isEditing}
              rows={3}
              className="resize-y"
            />
          </div>
        </div>

        <AnimatedDialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isEditing}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={isEditing}>
            {isEditing ? "Saving..." : "Save Changes"}
          </Button>
        </AnimatedDialogFooter>
      </AnimatedDialogContent>
    </AnimatedDialog>
  );
});
